// Bildschirm "Dein Zugang" (ST-SEC-001, ST-UX-001).
//
// Er zeigt die ECHTE Zugriffsentscheidung des angemeldeten Kontos:
// Browser -> Entra-Anmeldung -> Token -> apps/api -> Policy-Engine ->
// PostgreSQL -> zurueck. Kein Mockup, keine Beispieldaten.
//
// Aus main.tsx herausgeloest (ST-UX-001). Zwei Dinge sind dabei WEGGEFALLEN,
// und beide, weil der Rahmen sie besser traegt:
//
//   * Der Kontoname als Kartenkopf. Er steht jetzt im Kopfbereich und gilt fuer
//     die ganze Anwendung -- auf jedem Bildschirm zu wiederholen, wer man ist,
//     ist Wiederholung ohne Aussage.
//   * Der Abmeldeknopf. Auch er gehoert dem Rahmen. Waere er hier geblieben,
//     gaebe es ihn auf dem zweiten Bildschirm nicht -- und "abmelden geht nur
//     auf der Startseite" ist keine Regel, die jemand erraet.

import { useEffect, useState, type MouseEvent, type ReactElement } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Body1,
  Button,
  Caption1,
  Card,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from '@fluentui/react-components';

import { areaSummary, type PlainMessage } from '../access.ts';
import { fetchSearchOptions, type SearchOptionContext } from '../parties.ts';
import { SCREENS } from '../shell/navigation.ts';
import { navigate } from '../shell/router.tsx';
import { useScreenStyles } from '../shell/screen-styles.ts';
import type { AccessSnapshot } from '../shell/session.ts';
import { Vorgang } from '../shell/zustand.tsx';

/**
 * Wohin die Hauptaktion fuehrt, wenn die Uebersicht nicht lesbar ist.
 *
 * "Kontakte" und nicht "Kontakt anlegen": Lesen ist der Anfang jeder Arbeit
 * und die breiteste Rolle des Lesewegs (PARTY_READ_ROLE_CODE). Ob die Person
 * dort hinein darf, entscheidet der Server beim Oeffnen und sagt es ihr mit
 * einem uebersetzten Grund -- das ist der Angebotsmodus aus ADR-0028 Ziffer 3,
 * kein Versprechen. Aus SCREENS gelesen statt hier abgeschrieben, damit
 * Adresse und Beschriftung nicht auseinanderlaufen.
 */
const WEITER_ZU = SCREENS.find((screen) => screen.id === 'kontakte');

function StatusMessage({ snapshot }: { readonly snapshot: AccessSnapshot }): ReactElement {
  // intent traegt Farbe UND ein Symbol, und der Titel sagt denselben Zustand in
  // Worten. "Status nie nur ueber Farbe" (ADR-0002) und WCAG 1.4.1.
  //
  // `unreadable` ist weder gruen noch rot noch Warnung: die Uebersicht fehlt,
  // die Person ist nicht abgelehnt, und nichts ist gestoert. Gruen waere das
  // falsche Gruen aus ADR-0041 Ziffer 6, Rot sagte "du darfst nicht" zu
  // jemandem, der womoeglich alles darf, und Warngelb "mit dir stimmt etwas
  // nicht" zu jemandem, der einfach weiterarbeiten soll (Calm technology,
  // Pflichtenheft 5.2.14; Experience-Review 14.09.2026, Befund M2).
  const intent =
    snapshot.kind === 'permit'
      ? 'success'
      : snapshot.kind === 'unreadable'
        ? 'info'
        : snapshot.kind === 'failed'
          ? 'warning'
          : 'error';
  return (
    <MessageBar intent={intent}>
      <MessageBarBody>
        <MessageBarTitle>{snapshot.message.headline}</MessageBarTitle>
        <div>{snapshot.message.explanation}</div>
        <div>{snapshot.message.nextStep}</div>
      </MessageBarBody>
    </MessageBar>
  );
}

/**
 * Ob die technische Sicht ueberhaupt etwas zu zeigen hat.
 *
 * Sie stand bis zum 31.08.2026 IMMER da. Im Rueckfall enthielt sie eine
 * Vorgangsnummer, die als "nicht übermittelt" ausgewiesen war, und darunter die
 * Beschriftung "Antwort der Zugriffsprüfung" ohne Wert -- eine aufklappbare
 * Sicht, deren gesamter Inhalt die Auskunft war, dass es keine Auskunft gibt.
 * Progressive Offenlegung (NFR-UX-004) heisst, Vorhandenes zunaechst zu
 * verbergen, nicht Nichtvorhandenes anzukuendigen.
 */
export function hasTechnicalDetail(snapshot: AccessSnapshot): boolean {
  return snapshot.correlationId !== undefined || snapshot.requiredRoleCode !== undefined || snapshot.raw !== undefined;
}

/**
 * Die Hauptaktion, wenn die Uebersicht nicht lesbar ist: weiter zur Arbeit.
 *
 * Ein echter Verweis in Knopfgestalt -- `<a href>`, damit Screenreader,
 * Statusleiste, "in neuem Tab oeffnen" und Adresse kopieren funktionieren
 * (dieselbe Regel wie AppLink in router.tsx). Der gewoehnliche Linksklick
 * bleibt in der Anwendung; Mittelklick und Modifikatoren gehoeren dem Browser.
 */
function ContinueAction(): ReactElement | undefined {
  if (WEITER_ZU === undefined || WEITER_ZU.navLabel === undefined) {
    return undefined;
  }
  const to = WEITER_ZU.path;
  const onClick = (event: MouseEvent<HTMLElement>): void => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    navigate(to);
  };
  return (
    <Button as="a" href={to} appearance="primary" onClick={onClick}>
      Weiter zu {WEITER_ZU.navLabel}
    </Button>
  );
}

function TechnicalDetail({
  correlationId,
  requiredRoleCode,
  raw,
}: {
  readonly correlationId: string | undefined;
  readonly requiredRoleCode: string | undefined;
  readonly raw: unknown;
}): ReactElement {
  const styles = useScreenStyles();
  return (
    <Accordion collapsible>
      <AccordionItem value="technik">
        {/* Progressive Offenlegung (NFR-UX-004): zugeklappt vorbelegt. Wer sie
            nicht braucht, sieht sie nicht; wer den Durchstich pruefen will,
            findet hier den Beleg. */}
        <AccordionHeader>Technische Einzelheiten</AccordionHeader>
        <AccordionPanel>
          {/* Je Angabe: entweder Beschriftung MIT Wert oder gar nichts. Eine
              Beschriftung ohne Wert ist fuer eine Nutzerin keine Auskunft und
              fuer einen Screenreader eine Ansage ins Leere. */}
          {correlationId !== undefined ? (
            <div className={styles.detailRow}>
              <Caption1>Vorgangsnummer</Caption1>
              <p className={styles.detail}>{correlationId}</p>
            </div>
          ) : undefined}
          {requiredRoleCode !== undefined ? (
            // Der Code, um den die Administration gebeten wird. Hier und nicht
            // im Satz: fuer die Nutzerin ist er ein Technikbegriff (NFR-UX-003),
            // fuer die Administration die einzige eindeutige Angabe.
            <div className={styles.detailRow}>
              <Caption1>Fehlende Rolle</Caption1>
              <p className={styles.detail}>{requiredRoleCode}</p>
              {/* Die Bitte steht bei dem Code, um den sie sich dreht -- und
                  nicht als zweite Handlung im Hauptsatz. */}
              <Body1>Soll die Übersicht für dich funktionieren, kann deine Administration dir diese Rolle zuweisen.</Body1>
            </div>
          ) : undefined}
          {raw !== undefined ? (
            <>
              <Caption1>Antwort der Zugriffsprüfung</Caption1>
              <pre className={styles.detail}>{JSON.stringify(raw, null, 2)}</pre>
            </>
          ) : undefined}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}

export function AccessScreen({
  snapshot,
  busy,
  authProblem,
  authDetail,
  onRetry,
  apiBaseUrl,
  accessToken,
}: {
  readonly snapshot: AccessSnapshot | undefined;
  readonly busy: boolean;
  /**
   * Eine Stoerung VOR der Zugriffsentscheidung -- Anmeldung unbrauchbar oder
   * Anwendung nicht erreichbar.
   *
   * Als fertige `PlainMessage` und nicht als Zeichenkette: die erste Fassung
   * bekam hier einen Titel und entschied den Ratschlag daran, OB ER MIT
   * "LIAISON" BEGINNT. Das ist die Sorte Bedingung, die beim naechsten
   * umformulierten Satz stillschweigend den falschen Rat gibt -- und ein
   * falscher naechster Schritt kostet Arbeit und loest nichts (NFR-UX-005).
   * Wer den Fehler kennt, kennt auch den Rat; er gehoert dorthin.
   */
  readonly authProblem: PlainMessage | undefined;
  readonly authDetail: string | undefined;
  readonly onRetry: () => void;
  /**
   * Fuer die Namen der freigeschalteten Haeuser (A2, Entscheidung Dirigent 22.10.2026):
   * der bestehende Leseweg /party-search-options. Fehlt er oder scheitert er, zaehlt der
   * Satz nach Art; die Seite wartet nicht darauf.
   */
  readonly apiBaseUrl?: string | undefined;
  readonly accessToken?: (() => Promise<string | undefined>) | undefined;
}): ReactElement {
  const styles = useScreenStyles();
  const [bereiche, setBereiche] = useState<readonly SearchOptionContext[] | undefined>();
  const freigeschaltet = snapshot?.kind === 'permit';
  useEffect(() => {
    if (!freigeschaltet || apiBaseUrl === undefined || accessToken === undefined) return undefined;
    let aktuell = true;
    void (async () => {
      try {
        const token = await accessToken();
        if (token === undefined) return;
        const antwort = await fetchSearchOptions({ baseUrl: apiBaseUrl, accessToken: token });
        if (aktuell && antwort.outcome === 'loaded') setBereiche(antwort.operatingContexts);
      } catch {
        // Ohne Namen bleibt der Zaehlsatz; kein eigener Fehler auf einer Auskunftsseite.
      }
    })();
    return () => {
      aktuell = false;
    };
  }, [accessToken, apiBaseUrl, freigeschaltet]);

  if (busy) {
    return <Spinner labelPosition="after" label="Zugang wird geprüft" />;
  }

  return (
    <Card className={styles.card}>
      {/* Die Statusregion traegt aria-live und umfasst NUR das Ergebnis. Frueher
          lag aria-live auf dem gesamten Hauptbereich -- mit zwei Bildschirmen
          haette das bei jedem Wechsel die komplette neue Seite vorlesen lassen.
          Eine Live-Region gehoert um das, was sich aendert, nicht um alles. */}
      <div aria-live="polite" role="status">
        {authProblem !== undefined ? (
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>{authProblem.headline}</MessageBarTitle>
              <div>{authProblem.explanation}</div>
              <div>{authProblem.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : snapshot !== undefined ? (
          <>
            <StatusMessage snapshot={snapshot} />
            {snapshot.kind === 'permit' ? (
              <div className={styles.areas}>
                <Body1>{areaSummary(snapshot.areaCount, bereiche)}</Body1>
              </div>
            ) : undefined}
          </>
        ) : undefined}
      </div>
      {/* Hinter der Region (keine verschachtelte Live-Region); die technischen Einzelheiten behalten die Nummer zusaetzlich. */}
      {snapshot?.correlationId !== undefined && snapshot.message.nextStep.includes('Vorgangsnummer') ? <Vorgang nummer={snapshot.correlationId} eingebettet /> : undefined}

      <div className={styles.actions}>
        {/* "Erneut versuchen" erscheint nur, wo ein Versuch etwas aendern kann
            (NFR-UX-005). Ein Knopf, der verlaesslich nichts bewirkt, ist
            schlimmer als keiner: er laesst die Nutzerin die Schuld bei sich
            suchen. */}
        {snapshot?.message.retryable === true || authProblem?.retryable === true ? (
          <Button appearance="primary" onClick={onRetry}>
            Erneut versuchen
          </Button>
        ) : snapshot?.kind === 'unreadable' ? (
          // GENAU EINE Hauptaktion (NFR-UX-004), und hier ist es nicht der
          // Versuch, sondern der Weg zur Arbeit. Ohne sie endete die Karte in
          // einer Sackgasse: kein Knopf, und die Navigation als einziger,
          // unbenannter Ausweg.
          <ContinueAction />
        ) : undefined}
      </div>

      {authDetail !== undefined ? (
        <Accordion collapsible>
          <AccordionItem value="anmeldefehler">
            <AccordionHeader>Technische Einzelheiten</AccordionHeader>
            <AccordionPanel>
              <p className={styles.detail}>{authDetail}</p>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      ) : undefined}

      {snapshot !== undefined && hasTechnicalDetail(snapshot) ? (
        <TechnicalDetail
          correlationId={snapshot.correlationId}
          requiredRoleCode={snapshot.requiredRoleCode}
          raw={snapshot.raw}
        />
      ) : undefined}
    </Card>
  );
}
